import { classNames } from '@fullstory/util/fmt';
import React, { HTMLAttributes } from 'react';
import { FSMaskProp } from '../../types';
import css from './index.module.scss';

type ButtonType = 'submit' | 'button' | 'reset';

export interface ButtonBaseProps extends Omit<HTMLAttributes<HTMLButtonElement>, 'css'> {
  /**
   * For element identity  purpose. Default to 'button-base'
   */
  testId?: string;
  /**
   * The component used for the root node.
   * Either a string to use a DOM element or a component.
   */
  component?: React.ElementType;
  /**
   * The button type, if none is provided, it will default to "button"
   */
  type?: ButtonType;
  /**
   * If true, the button will not be able to be selected or clicked.
   */
  disabled?: boolean;
  children?: React.ReactNode;
  fsMask?: FSMaskProp;
}

interface ButtonConditionalProps {
  type?: ButtonType;
  disabled?: boolean;
  role?: string;
  'aria-disabled'?: boolean;
}

// eslint-disable-next-line react/display-name
export const ButtonBase = React.forwardRef<HTMLElement, ButtonBaseProps>(
  (
    {
      testId = 'button-base',
      component: Component = 'button',
      type = 'button',
      disabled = false,
      className,
      fsMask,
      ...rest
    },
    ref,
  ) => {
    const buttonProps: ButtonConditionalProps = {};
    if (Component === 'button') {
      buttonProps.type = type;
      buttonProps.disabled = disabled;
    } else {
      if (Component !== 'a') {
        buttonProps.role = 'button';
      }
      buttonProps['aria-disabled'] = disabled;
    }
    return (
      <Component
        data-component="ButtonBase"
        data-testid={testId}
        ref={ref}
        className={classNames(
          // TODO(craigmccown) The buttonReset class is only necessary to override existing legacy
          // styles. We should remove this when the app is converted.
          'buttonReset',
          css.buttonBase,
          fsMask,
          className,
          {
            [css.disabled]: disabled,
          },
        )}
        {...buttonProps}
        {...rest}
      />
    );
  },
);
